// cueUtils.js — shared DSL parsing utilities
// Used by cueDispatcher, animation, and any other module needing compound-ID splitting.

/** A DSL flag that is on: 1, "1", true or "true" (label:1 as label:true). */
export const truthy = (v) => [1, "1", true, "true"].includes(v);

/** A DSL flag that is explicitly off: 0, "0", false or "false". */
export const isOff = (v) => [0, "0", false, "false"].includes(v);

/**
 * Split a compound cue ID ("color(...) scale(...)") into individual expressions.
 * Paren-depth-aware: commas/spaces inside nested parens are not treated as splits.
 */
export function splitCueId(id) {
  if (!id || typeof id !== "string") return [];

  const results = [];
  let depth = 0;
  let start = 0;
  let inString = false;
  let stringChar = "";

  for (let i = 0; i < id.length; i++) {
    const ch = id[i];

    if ((ch === '"' || ch === "'") && !inString) {
      inString = true;
      stringChar = ch;
      continue;
    }
    if (inString && ch === stringChar) {
      inString = false;
      continue;
    }
    if (inString) continue;

    if (ch === "(") {
      depth++;
    } else if (ch === ")") {
      depth--;
      if (depth === 0) {
        const expr = id.slice(start, i + 1).trim();
        if (expr) results.push(expr);
        start = i + 1;
      }
    }
  }

  const tail = id.slice(start).trim();
  if (tail) results.push(tail);

  return results;
}

/**
 * Is `parent` a hand-authored extent group wrapping `el`?
 *
 * Inkscape users mark a cue's extent by grouping the cue element with a line
 * or path that spans the region. A small <g> holding the element plus such a
 * sibling is that idiom; "layer1" and the audio-objects layer are excluded
 * because they are containers, not extents.
 *
 * Shared because synth, audioImpulse and cueDispatcher each carried a
 * byte-identical private copy of this rule, which had to agree for a score to
 * behave the same whichever cue type read the extent.
 */
export function isAuthoredExtentGroup(parent, el) {
  if (!parent || parent.tagName?.toLowerCase() !== "g") return false;
  if (parent.id === "layer1") return false;
  const kids = parent.children;
  if (!kids || kids.length > 6) return false;
  for (const k of kids) {
    if (k === el) continue;
    const tag = k.tagName?.toLowerCase();
    if (tag === "line" || tag === "path") return true;
  }
  return false;
}

/**
 * tdelay, in seconds, from whichever shape a cue's AST happens to use.
 *
 * Three shapes exist and a cue uses exactly one of them, which is how the
 * parameter came to be read for some cues and silently ignored for the rest:
 * animation cues keep a flat field, the audio family a params object, and the
 * generic-param cues (fade, osc, text, ...) an args list of {type, value}.
 * Anything unreadable is no delay, never NaN — a bad number must not stop a
 * cue from firing at all.
 */
/**
 * chance:1/6 (a number 0..1), as readTdelay reads tdelay: from whichever of
 * its shapes the cue has. null when not given.
 */
export function readChance(ast) {
  if (!ast || typeof ast !== "object") return null;
  const raw = ast.chance ?? ast.params?.chance ??
    (Array.isArray(ast.args) ? ast.args.find(a => a?.type === "chance")?.value : undefined);
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : null;
}

/** One argument of a cue, from whichever of its shapes it has. */
function cueArg(ast, key) {
  return ast?.[key] ?? ast?.params?.[key] ?? (Array.isArray(ast?.args) ? ast.args.find(a => a?.type === key)?.value : undefined);
}

/** every:6 / every:peuclid(3, 8) as the cue has it (patterns.js makeEvery counts by it); null when not given. */
export function readEvery(ast) {
  return ast && typeof ast === "object" ? cueArg(ast, "every") ?? null : null;
}

/** A sound that steps its own patterns (synth.js): chance: and every: are its, step by step. */
export function stepsItself(ast) {
  return ast?.type === "cueSynth" && cueArg(ast, "dur") != null && cueArg(ast, "once") == null;
}

export function readTdelay(ast) {
  if (!ast || typeof ast !== "object") return 0;
  const raw =
    ast.tdelay ??
    ast.params?.tdelay ??
    (Array.isArray(ast.args) ? ast.args.find(a => a?.type === "tdelay")?.value : undefined);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * A pivot point from a cue parameter or a data-pivot attribute.
 *
 * Accepts the DSL's pair form, pivot:[x, y], and the string form the
 * data-pivot attribute has always used, "x,y" or "x y" — the attribute came
 * first and scores carry it, so the cue reads the same thing rather than a
 * second spelling of it.
 *
 * Returns {x, y} or null. A single number is null on purpose: pivot:40 could
 * mean either axis, and guessing would put the element somewhere the author
 * did not ask for.
 */
export function readPivot(value) {
  if (value == null) return null;
  let parts;
  if (Array.isArray(value)) parts = value;
  else if (typeof value === "number") return null;
  else parts = String(value).trim().split(/[\s,]+/);
  if (parts.length !== 2) return null;
  const x = Number(parts[0]), y = Number(parts[1]);
  return (Number.isFinite(x) && Number.isFinite(y)) ? { x, y } : null;
}

/**
 * A shape-led line with n: -- shape(n:12, ...) rotate(...) -- as one line
 * per member: n:12 goes, uid:<id> becomes uid:<id>_<i> plus
 * cluster:[<id>, <i>, <n>], and the rest of the line (the animations) is
 * repeated as is. Each member's rand() is then rolled on its own -- a
 * cluster of different shapes, as propagate() gives each child of a group
 * its own values. Returns null for anything that is not a cluster line.
 *
 *   shape(uid:sh1, n:3, sides:irand(3, 8)) rotate(dur:4)
 *   -> shape(uid:sh1_1, cluster:[sh1, 1, 3], sides:irand(3, 8)) rotate(dur:4)
 *      ... _2, _3
 *
 * `fallbackId` names a cluster written without a uid.
 */
export function expandCluster(line, fallbackId = null, random = Math.random) {
  const m = /^(\s*shape\s*\()/.exec(line);
  if (!m) return null;
  // the shape call's own text: up to its matching ")"
  let depth = 0, end = -1, quote = "";
  for (let i = m[1].length - 1; i < line.length; i++) {
    const ch = line[i];
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (ch === "(") depth++;
    else if (ch === ")" && --depth === 0) { end = i; break; }
  }
  if (end < 0) return null;
  const args = line.slice(m[1].length, end), rest = line.slice(end + 1);
  const nm = /(^|,)\s*n\s*:\s*(\d+|i?rand\(\s*(\d+)\s*,\s*(\d+)\s*\))\s*(?=,|$)/.exec(args);
  if (!nm) return null;
  let n = Number(nm[2]);
  if (!Number.isFinite(n)) {                       // n:irand(4, 9) -- rolled once
    const lo = Number(nm[3]), hi = Number(nm[4]);
    n = Math.floor(lo + random() * (hi - lo + 1));
  }
  n = Math.max(1, Math.min(200, Math.round(n)));
  const without = (args.slice(0, nm.index) + args.slice(nm.index + nm[0].length)).replace(/^\s*,\s*/, "");
  const um = /(^|,)\s*uid\s*:\s*([\w-]+)\s*(?=,|$)/.exec(without);
  const id = um ? um[2] : fallbackId;
  if (!id) return null;
  const body = um ? (without.slice(0, um.index) + without.slice(um.index + um[0].length)).replace(/^\s*,\s*/, "") : without;
  const out = [];
  for (let i = 1; i <= n; i++) {
    out.push(`${m[1]}uid:${id}_${i}, cluster:[${id}, ${i}, ${n}]${body.trim() ? ", " + body.trim() : ""})${rest}`);
  }
  return out;
}

/**
 * The name a use() run from the console goes by: the uid: written, else
 * the file's -- use(orion), use(src:constellations/orion.svg) -> orion --
 * else the folder's -- use(path:constellations, index:3) -> constellations,
 * so each run of that line swaps what is shown rather than adding another.
 */
export function useUid(args) {
  const get = k => args?.find(a => a?.type === k)?.value;
  const uid = get("uid");
  if (uid != null && typeof uid !== "object") return String(uid);
  const src = get("src"), path = get("path");
  let name = "";
  if (typeof src === "string") name = src.split("/").pop().replace(/\.svg$/i, "");
  else if (typeof path === "string") name = path.split("/").filter(Boolean).pop() || "";
  return name ? name.replace(/[^\w-]/g, "_") : null;
}
